/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useEffect, useLayoutEffect, lazy, Suspense, useRef, useState, type FormEvent } from 'react';
import { useCheckoutContext } from '@/hooks/use-checkout';
import { useBasket, useBasketHydrated } from '@/providers/basket';
import { useCheckoutActions, type PaymentSubmissionRef } from '@/hooks/use-checkout-actions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Typography } from '@/components/typography';
import { useCustomerProfile } from '@/hooks/checkout/use-customer-profile';
import type { ShopperBasketsV2, ShopperProducts, ShopperPromotions } from '@/scapi';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/providers/currency';
import { createPaymentSchema, type PaymentData } from '@/lib/checkout/schemas';
import type { AcimaCheckoutConfig } from '@/lib/checkout/loaders.server';
import { useAnalytics } from '@/hooks/use-analytics';
import { UITarget } from '@/targets/ui-target';
import { TERMS_AND_CONDITIONS_VERSION } from './components/terms-and-conditions';
import OrderSummary from '@/components/order-summary';
import { CHECKOUT_STEPS, type CheckoutStep } from './utils/checkout-context-types';
import {
    doesPaymentSelectionDiffer,
    handlePickupContinueAction,
    hasAnyValidShippingMethod,
} from './utils/checkout-utils';
import type { ShippingMethodDetailsContentAssets } from '@/components/checkout/components/shipping-method-details-modal';
import type { ContentAssetBody } from '@/lib/api/content-asset';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { filterDeliveryShippingMethods, getDeliveryShipment } from '@/extensions/bopis/lib/basket-utils';
// @sfdc-extension-block-end SFDC_EXT_BOPIS
import type { ConvergeSitePrefs } from '@/lib/shopper-config-context';
import DevPrefillButton, { type DevPrefillButtonProps } from './components/dev-prefill-button';
import { KountReadyProvider } from '@/providers/kount-ready';
import KountDataCollector from './components/kount-data-collector';
import PayPalZipErrorNotice from './components/paypal-zip-error-notice';
import { useLivePerson } from '@/providers/live-person';

// Lazy load heavy components
const ContactInfo = lazy(() => import('./components/contact-info'));
// @sfdc-extension-line SFDC_EXT_BOPIS
const CheckoutPickupWithData = lazy(() => import('@/extensions/bopis/components/checkout/checkout-pickup-with-data'));
// @sfdc-extension-block-start SFDC_EXT_MULTISHIP
const ShippingMultiAddressWithData = lazy(
    () => import('@/extensions/multiship/components/checkout/shipping-multi-address-with-data')
);
const ShippingMultiOptions = lazy(() => import('@/extensions/multiship/components/checkout/shipping-multi-options'));
// @sfdc-extension-block-end SFDC_EXT_MULTISHIP
const ShippingAddress = lazy(() => import('./components/shipping-address'));
const ShippingOptions = lazy(() => import('./components/shipping-options'));
// CHECKOUT-10 — per-fulfillment-group informational view; embeds the HD/THD
// radio toggle inline in each HD line item's footer.
const DeliveryGroups = lazy(() => import('./components/delivery-groups'));
const MattressRecyclingOptIn = lazy(() => import('./components/mattress-recycling-opt-in'));
const Payment = lazy(() => import('./components/payment'));
// CHECKOUT-14 — order review step card (items, T&C, place order CTA).
const OrderReviewStep = lazy(() => import('./components/order-review-step'));

// Import skeleton components for accurate loading states
import {
    CheckoutSkeleton,
    ContactInfoSkeleton,
    OrderSummarySkeleton,
    PaymentSkeleton,
    PickupSkeleton,
    ShippingAddressSkeleton,
    ShippingOptionsSkeleton,
} from './components/checkout-skeletons';

interface CheckoutFormPageProps {
    shippingMethodsMap: Record<string, ShopperBasketsV2.schemas['ShippingMethodResult']>;
    productMapPromise: Promise<Record<string, ShopperProducts.schemas['Product']>>;
    promotionsPromise?: Promise<Record<string, ShopperPromotions.schemas['Promotion']>>;
    showToast?: (message: string, type: 'success' | 'error', options?: { duration?: number }) => void;
    /**
     * Deferred BM content assets keyed by delivery method (see
     * `ShippingMethodDetailsAssetKey` in `shipping-method-details-modal.tsx`). Forwarded to both
     * `<DeliveryGroups/>` and `<ShippingOptions/>` and threaded into `<ShippingMethodDetailsModal/>`,
     * which selects the matching asset based on the active `(variant, methodName)` pair and
     * renders its HTML body. No i18n / Page Designer fallback — BM is the single source of truth.
     */
    shippingMethodDetailsContentAssets?: ShippingMethodDetailsContentAssets;
    /** Non-sensitive Acima iframe config; undefined when acima.enabled is false. */
    acimaConfig?: AcimaCheckoutConfig;
    /** Converge LightBox preferences resolved server-side; undefined when the feature is disabled or the BM hook is not deployed. */
    convergePrefs?: ConvergeSitePrefs;
    /** True while post-render ATP/Extend freshness validation is running. */
    freshnessPending?: boolean;
    /** Blocking freshness error to show at order-review / place-order. */
    freshnessError?: string | null;
    /** Mirrors the `enableSynchronyCVV` site preference — reveals + requires the Ashley Advantage security code (CVV). */
    synchronyCvvEnabled?: boolean;
    /**
     * BM content asset body for the Ashley Advantage / Synchrony Terms Agreement legal copy.
     * Forwarded to `<Payment/>` and into `<AshleyAdvantagePayment/>`, which renders it inside a
     * Suspense boundary on the Terms Agreement step. No in-code fallback — BM is the source of truth.
     */
    synchronyTermsAgreementContentAsset?: Promise<ContentAssetBody | null>;
    orderSummaryTaxHelpCopyContentAsset?: Promise<ContentAssetBody | null>;
}

type BasketWithMattressFlags = ShopperBasketsV2.schemas['Basket'] & {
    c_mattressRecyclingOptInRequired?: boolean;
};

/**
 * Wrapper component that resolves productMap and promotions Promises within Suspense boundary.
 */

export default function CheckoutFormPage({
    shippingMethodsMap: shippingMethodsMapFromLoader,
    productMapPromise,
    promotionsPromise,
    showToast,
    shippingMethodDetailsContentAssets,
    acimaConfig,
    convergePrefs,
    freshnessPending = false,
    freshnessError = null,
    synchronyCvvEnabled,
    synchronyTermsAgreementContentAsset,
    orderSummaryTaxHelpCopyContentAsset,
}: CheckoutFormPageProps) {
    const { t } = useTranslation('checkout');
    const { t: tErrors } = useTranslation('errors');
    const currency = useCurrency();

    const cart = useBasket();
    const basketWithMattressFlags = cart as BasketWithMattressFlags | null;
    const basketHydrated = useBasketHydrated();
    const { step, STEPS, goToStep, editingStep, shipmentDistribution, exitEditMode } = useCheckoutContext();
    // The step the shopper actually sees ("active step"): editingStep wins when set, otherwise the
    // basket-derived currentStep. Section open/closed state MUST key off this single value — using
    // `step === X || editingStep === X` can open two sections at once (e.g. a returning shopper at
    // PLACE_ORDER who edits Payment: editingStep=PAYMENT opens Payment while currentStep=PLACE_ORDER
    // keeps Review expanded). Exactly one step is active at a time.
    const activeStep = editingStep ?? step;
    const livePerson = useLivePerson();
    const customerProfile = useCustomerProfile();
    const isRegisteredUser = Boolean(customerProfile?.customer?.customerId);

    const paymentSubmissionRef = useRef<PaymentSubmissionRef['current']>({
        formDataGetter: null,
        shouldPlaceOrderAfterPayment: false,
        options: null,
        setFormErrors: null,
    });
    const otpFlowActiveRef = useRef(false);
    const [hideCreateAccountAfterSkippedPasswordlessOtp, setHideCreateAccountAfterSkippedPasswordlessOtp] =
        useState(false);
    // A fetcher retains its most recent response. Track the failed response that was dismissed
    // when the shopper explicitly reopens Payment, so it is not shown after a corrected payment.
    const [dismissedPlaceOrderError, setDismissedPlaceOrderError] = useState<unknown>(null);

    // DEV ONLY — prefill all checkout steps with test data; consumed by <Payment /> via `devPrefillCard`.
    const [devPrefillDefaults, setDevPrefillDefaults] = useState<
        Parameters<DevPrefillButtonProps['onPrefill']>[0] | null
    >(null);

    // Optimistic mirror of the Payment step's radio selection, expressed as a SCAPI
    // `paymentMethodId` (`'ACIMA_FINANCE'` | `'CREDIT_CARD'` | `'ASHLEY_FINANCE'` | `'PayPal'`
    // | `''`). `<Payment/>` fires `onPaymentMethodChange` on every radio change; the value is
    // forwarded to both `<OrderSummary/>` instances so the sales-tax row can hide (and the
    // displayed total drop) the instant the shopper picks Acima — parity with legacy SFRA
    // `ordertotals.isml` hiding the tax row when `pdict.isAcimaPayment`. Empty string means
    // "no selection yet"; OrderSummary then falls back to `basket.paymentInstruments`
    // (handles reloads with Acima already attached).
    const [tentativePaymentMethodId, setTentativePaymentMethodId] = useState<string>('');
    // CHECKOUT-14 — legal consent is implied at placement (Figma parity; no checkbox).
    // Keep forwarding the accepted/version payload so server-side validation remains intact.
    const termsAcceptanceRef = useRef<{ accepted: boolean; version: string }>({
        accepted: true,
        version: TERMS_AND_CONDITIONS_VERSION,
    });

    // Checkout actions hook with all fetchers and submission handlers
    const {
        submitContactInfo,
        submitShippingAddress,
        submitShippingOptions,
        submitPayment,
        submitPlaceOrder,
        contactFetcher,
        shippingAddressFetcher,
        shippingOptionsFetcher,
        paymentFetcher,
        placeOrderFetcher,
        isSubmitting,
    } = useCheckoutActions({ paymentSubmissionRef, otpFlowActiveRef, termsAcceptanceRef });

    const handleDevPrefill = useCallback<DevPrefillButtonProps['onPrefill']>(
        (data) => {
            setDevPrefillDefaults(data);
            submitContactInfo({
                email: data.contact.email,
            });
            const shippingFormData = new FormData();
            shippingFormData.append('firstName', data.shipping.firstName);
            shippingFormData.append('lastName', data.shipping.lastName);
            shippingFormData.append('address1', data.shipping.address1);
            shippingFormData.append('city', data.shipping.city);
            shippingFormData.append('stateCode', data.shipping.stateCode);
            shippingFormData.append('postalCode', data.shipping.postalCode);
            shippingFormData.append('countryCode', data.shipping.countryCode);
            // Phone + SMS consent moved from contact-info to the shipping
            // address step — forward them on the shipping submission instead.
            shippingFormData.append('phone', data.shipping.phone || data.contact.phone);
            shippingFormData.append('smsConsent', 'true');
            submitShippingAddress(shippingFormData);
        },
        [submitContactInfo, submitShippingAddress]
    );

    /**
     * Shopper closed passwordless OTP via "Checkout as guest" — do not verify OTP / sign in.
     * Unblock contact step and hide place-order create-account checkbox for this checkout session.
     */
    const handleRegisteredUserChoseGuest = useCallback(() => {
        setHideCreateAccountAfterSkippedPasswordlessOtp(true);
        otpFlowActiveRef.current = false;
        if (contactFetcher.state === 'idle' && contactFetcher.data?.success === true) {
            exitEditMode();
        }
    }, [contactFetcher.state, contactFetcher.data, exitEditMode]);

    /** After successful OTP verification at contact, restore create-account UI if still applicable */
    const handlePasswordlessOtpVerifiedAtContact = useCallback(() => {
        setHideCreateAccountAfterSkippedPasswordlessOtp(false);
    }, []);

    let showAddressAndOptions = true;

    // Determine shipping methods: prefer action response over loader data (avoids flash when advancing to shipping step)
    const actionShippingMethods = shippingAddressFetcher.data?.data?.shippingMethodsMap as
        | Record<string, ShopperBasketsV2.schemas['ShippingMethodResult']>
        | undefined;
    let shippingMethodsMap =
        actionShippingMethods && Object.keys(actionShippingMethods).length > 0
            ? actionShippingMethods
            : shippingMethodsMapFromLoader;

    // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
    let isDeliveryProductItem = (_item: ShopperBasketsV2.schemas['ProductItem']) => true;
    // @sfdc-extension-block-end SFDC_EXT_MULTISHIP

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    shippingMethodsMap = filterDeliveryShippingMethods(shippingMethodsMap);
    const hasPickupItems = shipmentDistribution.hasPickupItems;
    showAddressAndOptions = shipmentDistribution.hasDeliveryItems;
    isDeliveryProductItem = shipmentDistribution.isDeliveryProductItem;
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
    // const enableMultiAddress = shipmentDistribution.enableMultiAddress;
    const enableMultiAddress = false; // disabling multi address delivery
    const hasMultipleDeliveryAddresses = shipmentDistribution.hasMultipleDeliveryAddresses;
    const deliveryShipments = shipmentDistribution.deliveryShipments;
    // this tracks if the user pressed the multi address mode toggle button
    const [selectedMultiAddressMode, setSelectedMultiAddressMode] = useState(hasMultipleDeliveryAddresses);
    const handleToggleShippingAddressMode = () => {
        setSelectedMultiAddressMode((prev) => !prev);
    };
    // @sfdc-extension-block-end SFDC_EXT_MULTISHIP

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const { t: tBopis } = useTranslation('extBopis');
    const { label: pickupProceedButtonLabel, onClick: onPickupContinueClick } = handlePickupContinueAction(
        hasPickupItems,
        showAddressAndOptions,
        goToStep,
        STEPS,
        tBopis as (key: string) => string
    );
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    const analytics = useAnalytics();
    const hasTrackedCheckoutStartRef = useRef(false);
    const previousStepRef = useRef<CheckoutStep | null>(null);

    // Synchronize before paint so an already-available launcher cannot flash
    // while checkout transitions into or out of order review.
    useLayoutEffect(() => {
        livePerson.setCheckoutReviewActive(step === STEPS.PLACE_ORDER);
        return () => livePerson.setCheckoutReviewActive(false);
    }, [livePerson, step, STEPS.PLACE_ORDER]);

    useEffect(() => {
        // Only track checkout start once on mount if baseket is not empty
        if (!hasTrackedCheckoutStartRef.current && cart?.productItems && cart.productItems.length > 0) {
            void analytics.trackCheckoutStart({
                basket: cart,
            });
            hasTrackedCheckoutStartRef.current = true;
        }
    }, [analytics, cart]);

    useEffect(() => {
        if (previousStepRef.current !== step && cart?.productItems && cart.productItems.length > 0) {
            const stepName = Object.keys(STEPS).find((key) => STEPS[key as keyof typeof STEPS] === step) || '';
            void analytics.trackCheckoutStep({
                stepName,
                stepNumber: step,
                basket: cart,
            });
            previousStepRef.current = step;
        }
    }, [analytics, step, STEPS, cart]);

    // After a successful shipping-address submit that yields no deliverable shipping methods,
    // surface an error toast prompting the shopper to enter a different address. Guarded by the
    // fetcher data identity so the toast fires once per submit (not on unrelated re-renders).
    const noMethodsToastShownRef = useRef<unknown>(null);
    useEffect(() => {
        if (shippingAddressFetcher.state !== 'idle' || !shippingAddressFetcher.data?.success) return;
        if (noMethodsToastShownRef.current === shippingAddressFetcher.data) return;
        if (showAddressAndOptions && !hasAnyValidShippingMethod(shippingMethodsMap)) {
            noMethodsToastShownRef.current = shippingAddressFetcher.data;
            showToast?.(tErrors('checkout.noShippingMethodsForAddress'), 'error');
        }
    }, [
        shippingAddressFetcher.state,
        shippingAddressFetcher.data,
        shippingMethodsMap,
        showAddressAndOptions,
        showToast,
        tErrors,
    ]);

    const isPlacingOrder = placeOrderFetcher.state === 'submitting';
    const [isPlaceOrderPending, setIsPlaceOrderPending] = useState(false);
    const [basketSnapshotForPlaceOrder, setBasketSnapshotForPlaceOrder] = useState<
        ShopperBasketsV2.schemas['Basket'] | null
    >(null);

    // Form submission handlers - delegated to checkout actions hook
    const handleContactSubmit = submitContactInfo;
    const handleShippingAddressSubmit = submitShippingAddress;
    const handleShippingOptionsSubmit = submitShippingOptions;
    // "Continue to review" handler. When the basket already carries a tender that matches the
    // current selection — e.g. PayPal express attached the instrument out-of-band — there is
    // nothing to (re)submit; submitting the (empty) card form would be rejected server-side and
    // the button would appear dead. In that case just advance to the order-review step. Otherwise
    // submit the payment as usual (Converge card LightBox token, Acima, Ashley Advantage, etc.).
    const handlePaymentSubmit = useCallback(
        (data: PaymentData) => {
            const basketAlreadyHasPayment = Boolean(cart?.paymentInstruments?.[0]);
            if (basketAlreadyHasPayment && !doesPaymentSelectionDiffer(data, cart)) {
                exitEditMode();
                return;
            }
            submitPayment(data);
        },
        [cart, submitPayment, exitEditMode]
    );

    const handlePlaceOrderSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setBasketSnapshotForPlaceOrder(cart ?? null);
        const paymentData = paymentSubmissionRef.current.formDataGetter?.();
        const basketAlreadyHasPayment = Boolean(cart?.paymentInstruments?.[0]);

        // Payment must be submitted before place order when the basket has no instrument, or
        // when the user's current selection differs from what's on the basket (e.g. returning
        // shopper switched from saved card to "enter new card").
        const needsPaymentSync =
            paymentData && (!basketAlreadyHasPayment || doesPaymentSelectionDiffer(paymentData, cart));

        if (needsPaymentSync) {
            // Ashley Advantage requires an accepted financing term before the order can be placed.
            // The credit-card form is hidden for this method, so without this explicit guard the
            // empty hidden card fields fail schema validation and the button appears to do nothing.
            if (paymentData.paymentType === 'ashley-advantage' && !paymentData.ashleyTermCode) {
                paymentSubmissionRef.current.setFormErrors?.({
                    ashleyTermCode: {
                        type: 'validation',
                        message: t('ashleyAdvantage.termRequired', {
                            defaultValue: 'Please select and accept a financing term before placing your order.',
                        }),
                    },
                });
                goToStep(STEPS.PAYMENT);
                return;
            }

            // Client-side billing-address validation before submitting. Card data is captured in
            // the Converge LightBox (not inline fields), so only the billing rules are enforced
            // here — matching the checkout payment step. Server-side submit-payment keeps full
            // card/LightBox validation.
            if (!paymentData.useSavedPaymentMethod) {
                const schema = createPaymentSchema(t as (key: string) => string, {
                    skipCardFieldValidation: true,
                });
                const result = schema.safeParse(paymentData);
                if (!result.success) {
                    const { setFormErrors } = paymentSubmissionRef.current;
                    if (setFormErrors) {
                        const fieldErrors: Record<string, { type: string; message: string }> = {};
                        for (const issue of result.error.issues) {
                            const field = issue.path[0]?.toString();
                            if (field && !fieldErrors[field]) {
                                fieldErrors[field] = { type: 'validation', message: issue.message };
                            }
                        }
                        setFormErrors(fieldErrors);
                    }
                    goToStep(STEPS.PAYMENT);
                    return;
                }
            }

            setIsPlaceOrderPending(true);
            paymentSubmissionRef.current.shouldPlaceOrderAfterPayment = true;
            paymentSubmissionRef.current.options = {
                savePaymentToProfile: paymentData.savePaymentToProfile ?? false,
                useDifferentBilling: paymentData.billingSameAsShipping === false,
                // Ashley Advantage (Synchrony): forward the un-masked account number so the
                // place-order action can authorize against Synchrony. The basket only stores the
                // masked number, which Synchrony rejects — without this the order is created but
                // authorization fails, so the button appears to do nothing. When enableSynchronyCVV
                // is on (e.g. sandbox 002), the security code (CVV) is REQUIRED on the authorize
                // call — an empty value is rejected with 422 "SecurityCode is missing" — so it must
                // be forwarded here too (never persisted; used once for the single auth call).
                ...(paymentData.paymentType === 'ashley-advantage'
                    ? {
                          ashleyAccountNumber: paymentData.ashleyAccountNumber,
                          ashleyApgCode: paymentData.ashleyApgCode,
                          ashleyCvv: paymentData.ashleyCvv,
                          // Synchrony promo disclosure HTML captured at term acceptance — sent
                          // through to place-order so the authorize call can stamp it onto the
                          // order's finance T&C attribute without a second (sometimes-null) lookup.
                          ashleyDisclosureText: paymentData.ashleyDisclosureText,
                      }
                    : {}),
            };
            submitPayment(paymentData);
        } else {
            setIsPlaceOrderPending(true);
            paymentSubmissionRef.current.shouldPlaceOrderAfterPayment = false;
            paymentSubmissionRef.current.options = null;
            submitPlaceOrder();
        }
    };

    // Step state logic - centralized in container for single page layout
    // For single page layout: show all steps, current step is editable, completed steps show summary
    const contactInfoState = {
        isCompleted: step > STEPS.CONTACT_INFO,
        isEditing: activeStep === STEPS.CONTACT_INFO,
        onEdit: () => goToStep(STEPS.CONTACT_INFO),
    };

    const shippingAddressState = {
        isCompleted: step > STEPS.SHIPPING_ADDRESS,
        isEditing: activeStep === STEPS.SHIPPING_ADDRESS,
        onEdit: () => {
            goToStep(STEPS.SHIPPING_ADDRESS);
        },
    };

    const shippingOptionsState = {
        isCompleted: step > STEPS.SHIPPING_OPTIONS,
        isEditing: activeStep === STEPS.SHIPPING_OPTIONS,
        onEdit: () => goToStep(STEPS.SHIPPING_OPTIONS),
    };

    const handlePaymentEdit = useCallback(() => {
        if (placeOrderFetcher.data && !placeOrderFetcher.data.success) {
            setDismissedPlaceOrderError(placeOrderFetcher.data);
        }
        goToStep(STEPS.PAYMENT);
    }, [goToStep, placeOrderFetcher.data, STEPS.PAYMENT]);

    const paymentState = {
        isCompleted: step > STEPS.PAYMENT,
        isEditing: activeStep === STEPS.PAYMENT,
        onEdit: handlePaymentEdit,
        // Guest: show only "Payment" title until contact, shipping address and options are done
        disabled: !isRegisteredUser && step < STEPS.PAYMENT,
    };

    const placeOrderError =
        placeOrderFetcher.data &&
        placeOrderFetcher.data !== dismissedPlaceOrderError &&
        !placeOrderFetcher.data.success &&
        placeOrderFetcher.data.error
            ? placeOrderFetcher.data.error.message
            : undefined;

    // Focus place order error without scrolling (prevents CLS while maintaining accessibility)
    useEffect(() => {
        if (
            placeOrderFetcher.state === 'idle' &&
            placeOrderFetcher.data &&
            !placeOrderFetcher.data.success &&
            placeOrderFetcher.data.error
        ) {
            setIsPlaceOrderPending(false);
            setBasketSnapshotForPlaceOrder(null);
            // Find the error banner and focus it without scrolling
            const errorElement = document.querySelector('[role="alert"]');
            if (errorElement instanceof HTMLElement) {
                errorElement.tabIndex = -1;
                errorElement.focus({ preventScroll: true });
            }
        }
    }, [placeOrderFetcher.state, placeOrderFetcher.data]);

    // Place the order once payment succeeds; reset ref on failure so we never place order without valid payment
    useEffect(() => {
        if (!paymentSubmissionRef.current.shouldPlaceOrderAfterPayment || paymentFetcher.state !== 'idle') return;
        if (paymentFetcher.data?.success) {
            paymentSubmissionRef.current.shouldPlaceOrderAfterPayment = false;
            submitPlaceOrder();
        } else {
            paymentSubmissionRef.current.shouldPlaceOrderAfterPayment = false;
            setIsPlaceOrderPending(false);
            setBasketSnapshotForPlaceOrder(null);
        }
    }, [paymentFetcher.state, paymentFetcher.data, submitPlaceOrder]);

    if (!cart || !basketHydrated) {
        return <CheckoutSkeleton />;
    }

    if (!cart.basketId || !cart.productItems || cart.productItems.length === 0) {
        return (
            <div className="pb-20 lg:pb-0">
                <div className="section-container py-8">
                    <Typography variant="h3" as="h1" className="mb-10 md:mb-20 lg:mb-40">
                        {t('pageTitle')}
                    </Typography>
                    <div className="flex items-start justify-center">
                        <Card className="w-full max-w-md rounded-md py-8 shadow-md">
                            <CardContent className="p-6">
                                <Typography variant="muted" className="text-center">
                                    {t('common.emptyCart')}
                                </Typography>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    let shippingAddressComponent = (
        <ShippingAddress
            onSubmit={handleShippingAddressSubmit}
            isLoading={isSubmitting('shipping-address')}
            actionData={shippingAddressFetcher.data}
            // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
            enableMultiAddress={enableMultiAddress}
            handleToggleShippingAddressMode={handleToggleShippingAddressMode}
            // @sfdc-extension-block-end SFDC_EXT_MULTISHIP
            {...shippingAddressState}
        />
    );
    const defaultShipmentId = cart?.shipments?.[0]?.shipmentId ?? 'me';
    let shippingOptionsComponent = (
        <ShippingOptions
            onSubmit={handleShippingOptionsSubmit}
            isLoading={isSubmitting('shipping-options')}
            actionData={shippingOptionsFetcher.data}
            shippingMethods={shippingMethodsMap[defaultShipmentId]}
            shippingMethodDetailsContentAssets={shippingMethodDetailsContentAssets}
            {...shippingOptionsState}
        />
    );

    // CHECKOUT-10 — Show the new delivery-groups + HD/THD toggle alongside the OOTB
    // shipping-method picker only when (a) the basket has a delivery shipment (not
    // all-BOPIS) and (b) we're not in multi-address mode. The multi-address check is
    // applied below inside the SFDC_EXT_MULTISHIP block.
    let showCheckoutTenAddons = Boolean(getDeliveryShipment(cart));

    // @sfdc-extension-block-start SFDC_EXT_MULTISHIP
    // this is true if has multiple delivery addresses or user selected multi address mode and isediting addresses
    const isMultiAddressMode = shippingAddressState.isEditing ? selectedMultiAddressMode : hasMultipleDeliveryAddresses;
    if (isMultiAddressMode) {
        showCheckoutTenAddons = false;
        shippingAddressComponent = (
            <ShippingMultiAddressWithData
                isLoading={isSubmitting('shipping-address')}
                actionData={shippingAddressFetcher.data}
                productMapPromise={productMapPromise}
                isDeliveryProductItem={isDeliveryProductItem}
                deliveryShipments={deliveryShipments}
                handleToggleShippingAddressMode={handleToggleShippingAddressMode}
                onSubmit={handleShippingAddressSubmit}
                hasMultipleDeliveryAddresses={hasMultipleDeliveryAddresses}
                {...shippingAddressState}
            />
        );
        shippingOptionsComponent = (
            <ShippingMultiOptions
                onSubmit={handleShippingOptionsSubmit}
                isLoading={isSubmitting('shipping-options')}
                actionData={shippingOptionsFetcher.data}
                shipments={deliveryShipments}
                shippingMethodsMap={shippingMethodsMap}
                {...shippingOptionsState}
            />
        );
    }
    // @sfdc-extension-block-end SFDC_EXT_MULTISHIP

    // INT-12 (Sprint N+1): the standalone delivery-schedule step was removed.
    // OHDS scheduling now renders inline on each HD method tile via the
    // per-tile popover wired up inside <DeliveryMethodToggle/> (mounted
    // inside the per-line HD card of <DeliveryGroups/> — see CHECKOUT-10
    // and FE-08). No top-level gate here.
    const orderSummaryBasket =
        (isPlaceOrderPending || isPlacingOrder) && basketSnapshotForPlaceOrder ? basketSnapshotForPlaceOrder : cart;
    const showCaliforniaMattressAlert = basketWithMattressFlags?.c_mattressRecyclingOptInRequired === true;

    return (
        // CHECKOUT-17 — KountReadyProvider scopes the DDC readiness signal to the
        // checkout layout. <KountDataCollector/> (the writer) is mounted here at the
        // layout root rather than inside <Payment/>'s ToggleCardEdit so it survives
        // step transitions: when the top-of-checkout Express PayPal button
        // (<CheckoutExpressPayments/>) advances the flow from payment → review on
        // approve, the payment toggle collapses; if the DDC lived inside it the
        // useEffect cleanup would `clearTimeout` the 5-second safety timer and
        // `kountReady` would never flip — leaving the Place Order CTA stuck disabled
        // for the entire Express PayPal flow. (The in-step PayPal path inside
        // <Payment/> now pins to PAYMENT on approve and waits for the shopper to
        // click Continue, so it no longer participates in that race — but the
        // express path still requires the root-mounted DDC.) <OrderReviewStep/>
        // reads `useKountReady()` to gate the CTA. Outside this provider the hook
        // returns `true` (no-op) so non-checkout pages are unaffected.
        <KountReadyProvider>
            {/* CHECKOUT-17 — Kount DDC mount, scoped to the checkout layout. Renders
                a 1×1 off-screen aria-hidden iframe (or null when Kount is gated
                off); has no visible footprint anywhere it sits in the tree. */}
            <KountDataCollector />
            <div className="min-h-screen pb-20 lg:pb-0">
                <UITarget targetId="checkout.page.before" />
                <div className="section-container py-8">
                    <Typography variant="h3" as="h1" className="mb-6">
                        {t('pageTitle')}
                    </Typography>

                    {/* Mobile Order Summary + My Cart */}
                    <div className="mb-6 space-y-6 lg:hidden">
                        {/* `<OrderSummary>` wraps both the Card and `.TaxHelpCopy` because
                            `.TaxHelpCopy` reads `useOrderSummaryContext()` — placing it as a
                            sibling of the Card throws. `<Suspense>` sits above `<OrderSummary>`
                            because the root itself calls `use(promotionsPromise)` and needs a
                            boundary above the call site. */}
                        <Suspense fallback={<OrderSummarySkeleton />}>
                            <OrderSummary
                                basket={orderSummaryBasket}
                                promotionsPromise={promotionsPromise}
                                tentativePaymentMethodId={tentativePaymentMethodId}
                                shippingTbd={
                                    (orderSummaryBasket as unknown as Record<string, unknown>)
                                        ?.c_shippingMethodConfirmed !== true
                                }>
                                <Card className="relative rounded-md py-8 shadow-md gap-0">
                                    <CardContent className="p-0">
                                        <CardHeader>
                                            <CardTitle>
                                                <OrderSummary.Heading />
                                            </CardTitle>
                                        </CardHeader>
                                        <div className="divide-y divide-input">
                                            <div className="py-2">
                                                <OrderSummary.Subtotal />
                                                <OrderSummary.ServicesAndFees />
                                                <OrderSummary.PromoCodeForm borderPlacement="bottom" />
                                                <OrderSummary.Total />
                                            </div>
                                        </div>
                                        <OrderSummary.MyCart
                                            productMapPromise={productMapPromise}
                                            promotionsPromise={promotionsPromise}
                                        />
                                    </CardContent>
                                </Card>
                                <Suspense fallback={null}>
                                    <OrderSummary.TaxHelpCopy
                                        className="mt-4"
                                        contentAsset={orderSummaryTaxHelpCopyContentAsset}
                                    />
                                </Suspense>
                            </OrderSummary>
                        </Suspense>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 lg:gap-x-20">
                        {/* Main Checkout Content - Single Page Layout */}
                        <div className="lg:col-span-2 space-y-8">
                            <UITarget targetId="checkout.mainContent.before" />
                            {/* PayPal express ZIP-mismatch notice (legacy paypalAddressChangeNotice parity):
                                a dismissible banner shown when the shopper was routed here because the
                                PayPal-returned ship-to ZIP did not match the persisted delivery ZIP. */}
                            <PayPalZipErrorNotice />
                            <UITarget targetId="checkout.contactInfo.header.before" />
                            <Suspense fallback={<ContactInfoSkeleton />}>
                                <UITarget targetId="checkout.contactInfo.before" />
                                <UITarget targetId="checkout.contactInfo">
                                    {!cart ? (
                                        <ContactInfoSkeleton />
                                    ) : (
                                        <ContactInfo
                                            onSubmit={handleContactSubmit}
                                            isLoading={isSubmitting('contact')}
                                            actionData={contactFetcher.data}
                                            otpFlowActiveRef={otpFlowActiveRef}
                                            onRegisteredUserChoseGuest={handleRegisteredUserChoseGuest}
                                            onPasswordlessOtpVerified={handlePasswordlessOtpVerifiedAtContact}
                                            suppressRegisteredEmailLoginHints={
                                                hideCreateAccountAfterSkippedPasswordlessOtp
                                            }
                                            {...contactInfoState}
                                        />
                                    )}
                                </UITarget>
                                <UITarget targetId="checkout.contactInfo.after" />
                            </Suspense>

                            {/* @sfdc-extension-block-start SFDC_EXT_BOPIS */}
                            {/* Store Pickup Information */}
                            {hasPickupItems && (
                                <Suspense fallback={<PickupSkeleton />}>
                                    <CheckoutPickupWithData
                                        cart={cart}
                                        productMapPromise={productMapPromise}
                                        isEditing={editingStep === CHECKOUT_STEPS.PICKUP}
                                        onEdit={() => goToStep(CHECKOUT_STEPS.PICKUP)}
                                        onContinue={onPickupContinueClick}
                                        continueButtonLabel={pickupProceedButtonLabel}
                                    />
                                </Suspense>
                            )}

                            {/* @sfdc-extension-block-end SFDC_EXT_BOPIS */}

                            {/* Shipping Address & Options */}
                            {showAddressAndOptions && (
                                <>
                                    <UITarget targetId="checkout.shippingAddress.header.before" />
                                    <Suspense fallback={<ShippingAddressSkeleton />}>
                                        <UITarget targetId="checkout.shippingAddress.before" />
                                        <UITarget targetId="checkout.shippingAddress">
                                            {shippingAddressComponent}
                                        </UITarget>
                                        <UITarget targetId="checkout.shippingAddress.after" />
                                    </Suspense>

                                    {/* CHECKOUT-08: Mattress recycling opt-in (CA shoppers w/ mattress).
                                    Component itself early-returns null when not eligible — the
                                    eligibility check is server-computed in
                                    MattressRecyclingHelper.enrichBasketResponse and surfaced via
                                    basket.c_mattressRecyclingOptInRequired. */}
                                    <Suspense fallback={null}>
                                        <UITarget targetId="checkout.mattressRecycling">
                                            <MattressRecyclingOptIn />
                                        </UITarget>
                                    </Suspense>

                                    <UITarget targetId="checkout.shippingOptions.header.before" />
                                    <Suspense fallback={<ShippingOptionsSkeleton />}>
                                        <UITarget targetId="checkout.shippingOptions.before" />
                                        <UITarget targetId="checkout.shippingOptions">
                                            {/*
                                          CHECKOUT-10 — when the basket has a delivery shipment and
                                          we're in single-address (non-multiship) mode, render the
                                          new <DeliveryGroups/> only. The HD/THD radio is rendered
                                          per-line inside each HD card by <DeliveryGroups/>, and
                                          the OOTB SCAPI shipping-method (`standard-shipping` /
                                          `standard-shipping-ak-hi-pr`) is auto-applied by basket
                                          calculate — there is no separate picker to render.
                                          Falls back to the OOTB <ShippingOptions/> picker for
                                          all-BOPIS baskets and the multi-address branch.
                                        */}
                                            {showCheckoutTenAddons ? (
                                                <DeliveryGroups
                                                    productMapPromise={productMapPromise}
                                                    promotionsPromise={promotionsPromise}
                                                    shippingMethodDetailsContentAssets={
                                                        shippingMethodDetailsContentAssets
                                                    }
                                                    {...shippingOptionsState}
                                                />
                                            ) : (
                                                shippingOptionsComponent
                                            )}
                                        </UITarget>
                                        <UITarget targetId="checkout.shippingOptions.after" />
                                    </Suspense>
                                </>
                            )}

                            <UITarget targetId="checkout.payment.header.before" />
                            <Suspense fallback={<PaymentSkeleton />}>
                                <UITarget targetId="checkout.payment.before" />
                                <UITarget targetId="checkout.payment">
                                    <Payment
                                        onSubmit={handlePaymentSubmit}
                                        isLoading={isSubmitting('payment')}
                                        actionData={paymentFetcher.data}
                                        showBillingSameAsShipping={showAddressAndOptions}
                                        hidePaymentSaveCheckbox
                                        paymentSubmissionRef={paymentSubmissionRef}
                                        acimaConfig={acimaConfig}
                                        convergePrefs={convergePrefs}
                                        synchronyCvvEnabled={synchronyCvvEnabled}
                                        synchronyTermsAgreementContentAsset={synchronyTermsAgreementContentAsset}
                                        devPrefillCard={devPrefillDefaults?.card}
                                        onPaymentMethodChange={setTentativePaymentMethodId}
                                        {...paymentState}
                                    />
                                </UITarget>
                                <UITarget targetId="checkout.payment.after" />
                            </Suspense>

                            {/* CHECKOUT-14 — Order review step. The heading appears as soon as the
                            shopper reaches the payment step, but the full review (items, T&C, Place
                            Order CTA) is only revealed once payment has been submitted and the flow
                            advances to PLACE_ORDER — mirroring the collapsed/expanded behavior of the
                            other checkout steps and the legacy OPC "review before placement". */}
                            {step >= STEPS.PAYMENT && (
                                <Suspense fallback={null}>
                                    <OrderReviewStep
                                        step={activeStep}
                                        productMapPromise={productMapPromise}
                                        promotionsPromise={promotionsPromise}
                                        cart={cart}
                                        customerProfile={customerProfile}
                                        currency={currency}
                                        showCaliforniaMattressAlert={showCaliforniaMattressAlert}
                                        placeOrderError={placeOrderError}
                                        onPlaceOrderSubmit={handlePlaceOrderSubmit}
                                        isPlacingOrder={isPlacingOrder}
                                        isPlaceOrderPending={isPlaceOrderPending}
                                        isSubmittingPayment={isSubmitting('payment')}
                                        isPaymentFetcherSubmitting={paymentFetcher.state === 'submitting'}
                                        freshnessPending={freshnessPending}
                                        freshnessError={freshnessError}
                                        className="w-full lg:-mt-4"
                                    />
                                </Suspense>
                            )}
                            <UITarget targetId="checkout.mainContent.after" />
                        </div>

                        {/* Order Summary Sidebar - scrolls independently when content exceeds viewport */}
                        <div className="hidden lg:block col-span-1" data-testid="checkout-order-summary-sidebar">
                            <UITarget targetId="checkout.sidebar.before" />
                            <div className="sticky top-0 space-y-6">
                                {/* Order Summary
                                    `<OrderSummary>` wraps both the Card and `.TaxHelpCopy` because
                                    `.TaxHelpCopy` reads `useOrderSummaryContext()` — placing it as
                                    a sibling of the Card throws. `<Suspense>` sits above
                                    `<OrderSummary>` because the root itself calls
                                    `use(promotionsPromise)` and needs a boundary above the call
                                    site. */}
                                <Suspense fallback={<OrderSummarySkeleton />}>
                                    <OrderSummary
                                        basket={orderSummaryBasket}
                                        promotionsPromise={promotionsPromise}
                                        tentativePaymentMethodId={tentativePaymentMethodId}
                                        shippingTbd={
                                            (orderSummaryBasket as unknown as Record<string, unknown>)
                                                ?.c_shippingMethodConfirmed !== true
                                        }>
                                        <Card className="relative rounded-md py-8 shadow-md gap-0">
                                            <UITarget targetId="checkout.orderSummary.before" />
                                            <UITarget targetId="checkout.orderSummary">
                                                <CardHeader className="px-8 gap-0">
                                                    <CardTitle>
                                                        <OrderSummary.Heading />
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="p-0">
                                                    <OrderSummary.Subtotal />
                                                    <OrderSummary.MyCart
                                                        productMapPromise={productMapPromise}
                                                        promotionsPromise={promotionsPromise}
                                                    />
                                                    <div className="divide-y divide-input mb-0">
                                                        <div className="py-2">
                                                            <OrderSummary.ServicesAndFees />
                                                            <OrderSummary.Total />
                                                        </div>
                                                    </div>
                                                </CardContent>
                                            </UITarget>
                                            <UITarget targetId="checkout.orderSummary.after" />
                                        </Card>
                                        <Suspense fallback={null}>
                                            <OrderSummary.TaxHelpCopy
                                                className="mt-4"
                                                contentAsset={orderSummaryTaxHelpCopyContentAsset}
                                            />
                                        </Suspense>
                                    </OrderSummary>
                                </Suspense>
                            </div>
                            <UITarget targetId="checkout.sidebar.after" />
                        </div>
                    </div>
                </div>
                <UITarget targetId="checkout.page.after" />
                {process.env.NODE_ENV === 'development' && <DevPrefillButton onPrefill={handleDevPrefill} />}
            </div>
        </KountReadyProvider>
    );
}
